import { fileCalled } from '@nsb/core';
import { holdUpdate } from '../../update.ts';

/**
 * A drawing set as plates (P-36, #118; Jason, on Kalley Residence's Design reviews: "when a drawing set is uploaded as a
 * pdf, it needs to be broken apart into several plates for the design review so that comments and markups can be made").
 * Design study 28, A: "Split when added: every page of a PDF becomes a plate in the iteration, in order, and the PDF stays
 * beside them to open or download. Marking up works the way it already does for pictures." A plate is an ordinary picture
 * of the iteration, sent through the same door as one added by hand; only adding a PDF to a review iteration splits it.
 * Since P-37 (#125) the PDF no longer goes in beside its plates: "this really should be broken apart and not be a pdf
 * anymore" (`addDrawingSet`).
 *
 * The rules here are pure, so they are tested in Node; pdf.js reads and draws the PDF in `pdfSet.ts`.
 */

/** A plate's longest side in pixels: the most a picture is sent at for marking up (Reviews, `LONGEST`). */
export const PLATE_LONGEST = 4096;
/** Drawn as a JPEG at this quality, as a large picture is re-drawn for review. */
export const PLATE_QUALITY = 0.9;

/** Whether a file added to a review iteration is split into plates: a PDF, by its type or, without one, its name. */
export function splitsIntoPlates(file: { type: string; name: string }): boolean {
  return file.type === 'application/pdf' || (!file.type && /\.pdf$/i.test(file.name));
}

/** The scale a page is drawn at, from its size at scale 1, and the plate's size: its longest side `PLATE_LONGEST`. */
export function plateSize(page: { width: number; height: number }): { scale: number; width: number; height: number } {
  const scale = PLATE_LONGEST / Math.max(page.width, page.height);
  return { scale, width: Math.min(PLATE_LONGEST, Math.round(page.width * scale)), height: Math.min(PLATE_LONGEST, Math.round(page.height * scale)) };
}

/**
 * A page of a PDF shown on its page in Files (L-35): as wide as the box it fills on the screen's own pixels (`cssWidth`
 * times the device's pixel ratio), never past a plate's longest side.
 */
export function pageDrawSize(page: { width: number; height: number }, cssWidth: number, dpr: number): { scale: number; width: number; height: number } {
  const scale = Math.min((cssWidth * dpr) / page.width, PLATE_LONGEST / Math.max(page.width, page.height));
  return { scale, width: Math.round(page.width * scale), height: Math.round(page.height * scale) };
}

/** A bookmark of the PDF's outline, on the page it opens. */
export interface SheetMark {
  page: number;
  depth: number;
  title: string;
}

/** How long a sheet's title may be. */
const TITLE_MOST = 120;
/** Words that say only which page it is ("Page 2", "Sheet 4", "Bookmark 1"). */
const ONLY_A_NUMBER = /^(?:page|pg\.?|p\.?|sheet|bookmark|untitled)?\s*\d*$/i;
/** A page label that is only the page's number, in figures or in roman numerals. */
const A_NUMBER = /^(?:\d+|[ivxlcdm]+)$/i;

function cleaned(text: string | null | undefined): string | null {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!t || ONLY_A_NUMBER.test(t)) return null;
  if (t.length <= TITLE_MOST) return t;
  const cut = t.slice(0, TITLE_MOST);
  return `${cut.slice(0, cut.lastIndexOf(' ') > 40 ? cut.lastIndexOf(' ') : TITLE_MOST - 1)}…`;
}

const plain = (t: string) => t.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Each sheet's title, from what the drawing says of it: the deepest bookmark on its page (a sheet's own, not its
 * section's), with the page's label in front when the label is a word and the bookmark does not carry it ("A-102" and
 * "Elevations"), else the one there is; null where the drawing names it neither way. A title two sheets share says which
 * of the set each is.
 */
export function titlesFrom(count: number, labels: readonly string[] | null, marks: readonly SheetMark[]): (string | null)[] {
  const deepest = new Map<number, SheetMark>();
  for (const m of marks) {
    const title = cleaned(m.title);
    const held = deepest.get(m.page);
    if (title && (!held || m.depth > held.depth)) deepest.set(m.page, { ...m, title });
  }
  const titles = Array.from({ length: count }, (_, i) => {
    const raw = labels?.[i]?.trim() ?? '';
    const label = raw && !A_NUMBER.test(raw) ? cleaned(raw) : null;
    const mark = deepest.get(i)?.title ?? null;
    if (label && mark) return plain(mark).includes(plain(label)) ? mark : cleaned(`${label} ${mark}`);
    return mark ?? label;
  });
  return titles.map((t, i) => (t && titles.filter((u) => u === t).length > 1 ? `${t} (sheet ${i + 1} of ${count})` : t));
}

/** What sheetTitles reads of a PDF: pdf.js's document, or anything that answers as it does. */
export interface SheetSource {
  numPages: number;
  getPageLabels(): Promise<string[] | null>;
  getOutline(): Promise<OutlineItem[] | null>;
  getDestination(id: string): Promise<unknown[] | null>;
  getPageIndex(ref: never): Promise<number>;
}
interface OutlineItem {
  title: string;
  dest: string | unknown[] | null;
  items: OutlineItem[];
}

/** Each sheet's title from a PDF's page labels and outline (`titlesFrom`); a PDF that names its sheets neither way has none. */
export async function sheetTitles(doc: SheetSource): Promise<(string | null)[]> {
  const labels = await doc.getPageLabels().catch(() => null);
  const outline = await doc.getOutline().catch(() => null);
  const pageOf = async (dest: OutlineItem['dest']): Promise<number | null> => {
    try {
      const explicit = typeof dest === 'string' ? await doc.getDestination(dest) : dest;
      const ref = Array.isArray(explicit) ? explicit[0] : null;
      if (typeof ref === 'number') return ref;
      return ref && typeof ref === 'object' ? await doc.getPageIndex(ref as never) : null;
    } catch {
      return null;
    }
  };
  const marks: SheetMark[] = [];
  const walk = async (items: OutlineItem[] | null | undefined, depth: number) => {
    for (const item of items ?? []) {
      const page = await pageOf(item.dest);
      if (page !== null && page >= 0 && page < doc.numPages) marks.push({ page, depth, title: item.title });
      await walk(item.items, depth + 1);
    }
  };
  await walk(outline, 0);
  return titlesFrom(doc.numPages, labels, marks);
}

/** The most a file's name may be (the API's). */
const NAME_MOST = 240;

/**
 * A plate's name, read wherever files are named (Files, the review's list, the markup page, the timeline): its sheet,
 * by the drawing's title or "Sheet 3 of 6", then its set, by the PDF's name, or the review's when the PDF's is only an id.
 */
export function plateName(sheet: { index: number; count: number; title: string | null }, pdf: { name: string; review: string }): string {
  const first = sheet.title ?? `Sheet ${sheet.index + 1} of ${sheet.count}`;
  const set = fileCalled(pdf.name) ? pdf.name.trim().replace(/\.pdf$/i, '').trim() : pdf.review;
  const name = `${first}, ${set}`;
  return name.length <= NAME_MOST ? name : `${name.slice(0, NAME_MOST - 1)}…`;
}

/** What the page says while a set splits. */
export const splitting = (pdfName: string, index: number, count: number) => `Making plates of ${pdfName}: sheet ${index + 1} of ${count}`;

/** Why pdf.js could not read a PDF, in words. */
function unreadable(e: unknown): string {
  const name = e && typeof e === 'object' && 'name' in e ? String((e as { name: unknown }).name) : '';
  if (name === 'PasswordException') return 'it is locked with a password';
  if (name === 'InvalidPDFException') return 'it does not read as a PDF';
  return 'its pages could not be read';
}

/** A PDF in Files whose pages cannot be drawn (L-35): the page says why, and keeps the file a click away. */
export const pagesFailed = (pdfName: string, e: unknown) => `${pdfName} cannot be shown here: ${unreadable(e)}.`;

/** A PDF that cannot be read stays added as one file, and the page says why. */
export const splitFailed = (pdfName: string, e: unknown) => `${pdfName} is in the review as one file, not split into plates: ${unreadable(e)}.`;

/** A set that stopped partway: the plates made stay, and the PDF goes in whole beside them. */
export const plateFailed = (pdfName: string, made: number, count: number, why: string) =>
  `${pdfName}: ${made} of its ${count} sheets are plates; sheet ${made + 1} could not be made (${why}). The PDF is in the review whole.`;

/** What splits a PDF did not load (offline, or a release changed under the page): the PDF stays one file. */
export const readerFailed = (pdfName: string) =>
  `${pdfName} is in the review as one file, not split into plates: the page could not load what splits it. Reload the page, take the PDF out and add it again.`;

/** What splits a PDF could not be loaded (`readerFailed`). */
export class ReaderMissing extends Error {}

/** A PDF opened to split (pdfSet.ts `PdfSet`), or anything that answers as it does. */
export interface PlateSource {
  count: number;
  titles: (string | null)[];
  plate(index: number): Promise<Blob>;
  close(): Promise<void>;
}

/**
 * A drawing set added to a review iteration (P-36; P-37, #125, Jason: "this really should be broken apart and not be a
 * pdf anymore"): its sheets go in as plates, in order, and the PDF does not go in beside them. Only a set that cannot be
 * split, or stops partway, goes in whole, so nothing is lost, and the page says why. The app's own update is held while
 * it splits, so a new release never reloads the page under it (update.ts). What went wrong, in words; null when every
 * sheet is a plate.
 */
export async function addDrawingSet(
  pdfName: string,
  review: string,
  doors: {
    /** Load what splits it (ReaderMissing when it cannot) and open the set; pdf.js's refusal when it cannot be read. */
    open: () => Promise<PlateSource>;
    sendPlate: (name: string, plate: Blob) => Promise<void>;
    sendWhole: () => Promise<void>;
    saying: (words: string) => void;
  },
): Promise<string | null> {
  const release = holdUpdate();
  try {
    let opened: PlateSource;
    try {
      opened = await doors.open();
    } catch (e) {
      await doors.sendWhole();
      return e instanceof ReaderMissing ? readerFailed(pdfName) : splitFailed(pdfName, e);
    }
    let made = 0;
    try {
      for (; made < opened.count; made++) {
        doors.saying(splitting(pdfName, made, opened.count));
        const name = plateName({ index: made, count: opened.count, title: opened.titles[made] ?? null }, { name: pdfName, review });
        await doors.sendPlate(name, await opened.plate(made));
      }
      return null;
    } catch (e) {
      await doors.sendWhole();
      // Refused by the API in its words; else the browser could not draw it.
      return plateFailed(pdfName, made, opened.count, refused(e));
    } finally {
      await opened.close().catch(() => undefined);
    }
  } finally {
    release();
  }
}

/** Why a plate was not made, in words: the API's refusal (413: too big), else the drawing. */
function refused(e: unknown): string {
  const status = e && typeof e === 'object' && 'status' in e ? (e as { status: unknown }).status : null;
  if (status === 413) return 'too big to send';
  return typeof status === 'number' && e instanceof Error ? e.message : 'it could not be drawn';
}
